import React from 'react';
import { string, shape, number, object } from 'prop-types';
import polyline from '@mapbox/polyline';

import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { circlePolyline } from '../../util/maps';
import { apiBaseUrl } from '../../util/api';

const formatColor = color => {
  return color.replace(/^#/, '');
};

const fuzzyCircleOverlay = (center, mapsConfig) => {
  const strokeWeight = 1;
  const strokeColor = mapsConfig.fuzzy.circleColor;
  const strokeOpacity = 0.5;
  const fillColor = mapsConfig.fuzzy.circleColor;
  const fillOpacity = 0.2;

  const path = circlePolyline(center, mapsConfig.fuzzy.offset);
  const styles = `-${strokeWeight}+${formatColor(strokeColor)}-${strokeOpacity}+${formatColor(
    fillColor
  )}-${fillOpacity}`;
  return `path${styles}(${encodeURIComponent(polyline.encode(path))})`;
};

const markerOverlay = center => {
  return `pin-s(${center.lng},${center.lat})`;
};

const mapOverlay = (center, mapsConfig) => {
  if (mapsConfig.fuzzy.enabled) {
    return fuzzyCircleOverlay(center, mapsConfig);
  }
  return markerOverlay(center);
};

// Canonical request size sent to Mapbox regardless of the rendered container
// size. Using a single size means every visitor of the same listing hits the
// same S3 cache key. The image is decorative and gets visually scaled by CSS,
// so the on-screen quality is unaffected. 640x320 is large enough for retina
// on mobile and small enough to stay around ~75 KB.
const CANONICAL_WIDTH = 640;
const CANONICAL_HEIGHT = 320;

const StaticMapboxMap = props => {
  const { address, center, zoom, mapsConfig, dimensions } = props;
  const { width, height } = dimensions;

  const libLoaded = typeof window !== 'undefined' && window.mapboxgl;
  if (!libLoaded) {
    return null;
  }

  const overlay = mapOverlay(center, mapsConfig);
  // Go through our backend cache (server/api/staticMap.js) so identical
  // requests hit S3 instead of re-billing Mapbox per pageview.
  const params = new URLSearchParams({
    lng: center.lng,
    lat: center.lat,
    zoom,
    w: CANONICAL_WIDTH,
    h: CANONICAL_HEIGHT,
  });
  if (overlay) {
    params.set('overlay', overlay);
  }
  const src = `${apiBaseUrl()}/api/static-map?${params.toString()}`;

  // Preserve the measured aspect ratio of the container by stretching the img
  // to fill it. We keep width/height as render hints for the layout but they
  // do not change the upstream Mapbox request.
  const style = width && height ? { width: '100%', height: '100%', objectFit: 'cover' } : undefined;

  return <img src={src} loading="lazy" alt="" style={style} />;
};

StaticMapboxMap.defaultProps = {
  address: '',
  center: null,
};

StaticMapboxMap.propTypes = {
  address: string,
  center: shape({
    lat: number.isRequired,
    lng: number.isRequired,
  }).isRequired,
  zoom: number.isRequired,
  mapsConfig: object.isRequired,

  // from withDimensions
  dimensions: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

export default lazyLoadWithDimensions(StaticMapboxMap, { maxWidth: '640px' });
